import { useState, useEffect } from 'react'
import { AnimatePresence } from 'framer-motion'
import { useDatasetDataContext } from '@datasets/dataset-details/datasetData/context'
import { ActionType } from '@datasets/dataset-details/datasetData/context/types'
import { api } from 'api'
import { CreateSubmission, EditSelectRows, ExportTemplateData, ImportFile } from './actionButtons'
import { DownloadOption } from './types'
import { t } from '@lingui/core/macro'
import { useAppSelector } from '@redux/hooks'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'

interface Props {
  manualEditDisabled: boolean
  numSelectedRows: number
  removingRows: boolean
  shouldShowEditConfirmModal: boolean
  removeRows: () => Promise<void>
  manualEditRows: () => Promise<void>
  duplicateRows: () => void
  datasetRowCount: number
}

/**
 * Header for the template details view -> /template/:id/data
 */
export const TemplateDetailsHeader = ({
  manualEditDisabled,
  numSelectedRows,
  removingRows,
  removeRows,
  manualEditRows,
  duplicateRows,
  datasetRowCount,
  shouldShowEditConfirmModal,
}: Props) => {
  const { dispatch } = useDatasetDataContext()
  const [downloadOptionsLoading, setDownloadOptionsLoading] = useState<boolean>(true)
  const [downloadOptions, setDownloadOptions] = useState<DownloadOption[]>([])
  const templateId = window.location.pathname.split('/')[2]
  const [DATA_RELATIONSHIP_ENABLED = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.DATA_RELATIONSHIP]),
  )

  useEffect(() => {
    dispatch({ type: ActionType.editMode, payload: { isEnabled: true } })

    void api
      .get<
        {
          config_hash: string
          name: string
          slug: string
        }[]
      >(`/api/v1/integration-assignments/template/${templateId}`)
      .then(({ data }) => {
        const downOpts: DownloadOption[] = [
          {
            id: 'current',
            name: t`Current View`,
            type: 'hide',
            exportConfigurationId: null,
          },
        ]

        if (DATA_RELATIONSHIP_ENABLED) {
          downOpts.push({
            id: 'all-data-relationship',
            name: t`All Data w/ Relationships`,
            type: 'hide',
            exportConfigurationId: null,
          })
        }

        data.forEach(dl => {
          if (
            dl !== undefined &&
            [
              'box-push-general',
              's3-push',
              'azure-blob-push',
              'sftp-push',
              'google-cloud',
              'email-output',
              'snowflake-push',
            ].includes(dl.slug)
          ) {
            downOpts.push({
              id: dl.config_hash,
              name: dl.name || '',
              type: dl.slug,
              exportConfigurationId: null,
            })
          }
        })

        setDownloadOptions(downOpts)
        setDownloadOptionsLoading(false)
      })
  }, [])

  if (!templateId) {
    return null
  }

  return numSelectedRows > 0 ? (
    <EditSelectRows
      isRollup
      manualEditDisabled={manualEditDisabled}
      numSelectedRows={numSelectedRows}
      manualEditRows={manualEditRows}
      removeRows={removeRows}
      removingRows={removingRows}
      duplicateRows={duplicateRows}
      shouldShowEditConfirmModal={shouldShowEditConfirmModal}
    />
  ) : (
    <AnimatePresence>
      <CreateSubmission key="createSubmission" templateId={templateId} />
      <ImportFile key="importFile" />
      <ExportTemplateData
        key="exportDatasetData"
        downloadOptions={downloadOptions}
        downloadOptionsLoading={downloadOptionsLoading}
        datasetRowCount={datasetRowCount}
      />
    </AnimatePresence>
  )
}
